import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, ChevronLeft, ChevronRight, Trash2, ArrowUpDown, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/hooks/useOptions";
import { useTenants } from "@/hooks/useTenants";
import { PageHeader } from "@/components/kit/PageHeader";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LogDetailDrawer } from "@/components/logs/LogDetailDrawer";
import { fmtDateTime, isoDayInTz } from "@/lib/format";

const ALL = "all";
const isoDay = (d) => isoDayInTz(d);
const defaultRange = () => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { from: isoDay(from), to: isoDay(to) };
};

function ClearLogsDialog({ open, onOpenChange, isSuper, tenantOpts, activeType }) {
  const qc = useQueryClient();
  const [type, setType] = useState(activeType);
  const [tenant, setTenant] = useState(ALL);
  const [range, setRange] = useState({ from: "", to: "" });
  const [busy, setBusy] = useState(false);
  const [onlyDiag, setOnlyDiag] = useState(false);

  // Sync internal type with activeType prop when dialog opens
  useEffect(() => { if (open) { setType(activeType); setOnlyDiag(false); } }, [open, activeType]);

  const run = async () => {
    setBusy(true);
    try {
      const res = await http.post("/logs/clear", {
        log_type: type, tenant_id: isSuper ? tenant : undefined,
        date_from: range.from || undefined, date_to: range.to || undefined,
        only_diagnostic: type === "inbound" ? onlyDiag : undefined,
      });
      toast.success(`Cleared ${res.data.deleted} log record(s)`);
      qc.invalidateQueries({ queryKey: ["logs"] });
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to clear logs"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/[0.1] bg-[#141417] sm:max-w-md" data-testid="clear-logs-dialog">
        <DialogHeader><DialogTitle className="text-foreground">Clear logs</DialogTitle>
          <DialogDescription className="text-muted-foreground">Choose a log type and optional period to delete matching records.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-[#EF4444]/30 bg-[#EF4444]/[0.06] p-3 text-sm text-[#fca5a5]">
            This permanently deletes matching <b>log records</b>. It is irreversible. Business data
            (offers, streams, conversions, finance) is never touched.
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Log type</label>
            <SearchSelect value={type} onChange={setType} testId="clear-type"
              options={[{ value: "system", label: "System logs" }, { value: "inbound", label: "Incoming postbacks" }, { value: "outbound", label: "Outgoing / S2S postbacks" }]} widthClass="w-full" />
          </div>
          {isSuper && (
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Tenant</label>
              <SearchSelect value={tenant} onChange={setTenant} options={tenantOpts} testId="clear-tenant" widthClass="w-full" />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Period (optional — leave empty to clear all)</label>
            <DateRangePicker from={range.from} to={range.to} onChange={setRange} testId="clear-range" />
          </div>
          {type === "inbound" && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={onlyDiag} onChange={(e) => setOnlyDiag(e.target.checked)}
                data-testid="clear-only-diagnostic" className="h-3.5 w-3.5 accent-white" />
              Only diagnostic records (not_matched) — keep real conversions
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          <Button onClick={run} disabled={busy} data-testid="clear-logs-confirm" className="gap-1.5 bg-[#EF4444] text-white hover:bg-[#dc2626]"><Trash2 className="h-4 w-4" /> Clear logs</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Logs() {
  const { user } = useAuth();
  const isSuper = (user?.permissions || []).includes("*");
  const isBuyer = user?.role === "buyer";
  const canClear = isSuper || user?.role === "tenant_admin";

  const opts = useOptions();
  const { data: tenantsData } = useTenants();
  const tenantOpts = useMemo(() => [{ value: ALL, label: "All tenants" }, ...((tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })))], [tenantsData]);
  const userOpts = useMemo(() => [{ value: ALL, label: "All users" }, ...[...(opts.buyers || []), ...(opts.managers || [])].map((u) => ({ value: u.id, label: `${u.tenant_seq != null ? `#${u.tenant_seq} ` : ""}${u.name}`, search: `#${u.tenant_seq} ${u.tenant_seq ?? ""} ${u.name} ${u.email || ""}` }))], [opts]);
  const buyerOpts = useMemo(() => [{ value: ALL, label: "All buyers" }, ...(opts.buyers || []).map((u) => ({ value: u.id, label: `${u.tenant_seq != null ? `#${u.tenant_seq} ` : ""}${u.name}`, search: `#${u.tenant_seq} ${u.tenant_seq ?? ""} ${u.name} ${u.email || ""}` }))], [opts]);
  const advOpts = useMemo(() => [{ value: ALL, label: "All advertisers" }, ...(opts.advertisers || []).map((a) => ({ value: a.id, label: a.name, search: a.name }))], [opts]);

  const TABS = isBuyer ? [{ key: "outbound", label: "Outgoing / S2S" }]
    : [{ key: "system", label: "System logs" }, { key: "inbound", label: "Incoming postbacks" }, { key: "outbound", label: "Outgoing / S2S" }];
  const [tab, setTab] = useState(TABS[0].key);
  const [tenant, setTenant] = useState(ALL);
  const [uid, setUid] = useState(ALL);
  const [advId, setAdvId] = useState(ALL);
  const [buyerId, setBuyerId] = useState(ALL);
  const [range, setRange] = useState(defaultRange);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [showDiag, setShowDiag] = useState(false);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize, sort };
    if (q.trim()) p.q = q.trim();
    if (range.from) p.date_from = range.from;
    if (range.to) p.date_to = range.to;
    if (tab === "inbound" && showDiag) p.diagnostics = "1";
    if (isSuper && tenant !== ALL) p.tenant_id = tenant;
    if (!isSuper && !isBuyer) {
      if (tab === "system" && uid !== ALL) p.user_id = uid;
      if (tab === "inbound") { if (advId !== ALL) p.advertiser_id = advId; if (buyerId !== ALL) p.buyer_id = buyerId; }
      if (tab === "outbound" && buyerId !== ALL) p.buyer_id = buyerId;
    }
    return p;
  }, [page, sort, q, range, isSuper, tenant, isBuyer, tab, uid, advId, buyerId, pageSize, showDiag]);

  const { data, isLoading } = useQuery({
    queryKey: ["logs", tab, params],
    queryFn: async () => (await http.get(`/logs/${tab}`, { params })).data,
    keepPreviousData: true,
  });
  const rows = data?.items || [];
  const total = data?.total || 0;
  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  const changeTab = (k) => { setTab(k); setPage(1); setExpanded(null); };
  const resetFilters = () => { setTenant(ALL); setUid(ALL); setAdvId(ALL); setBuyerId(ALL); setRange(defaultRange()); setQ(""); setPage(1); };

  const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60";
  const td = "px-3 py-2.5 align-top text-sm text-foreground/90";

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Logs" subtitle="System audit, incoming and outgoing (S2S) postback logs." />
        {canClear && (
          <Button variant="outline" onClick={() => setClearOpen(true)} data-testid="open-clear-logs"
            className="gap-1.5 border-white/[0.12] text-muted-foreground hover:text-foreground"><Trash2 className="h-4 w-4" /> Clear logs</Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/[0.06]">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => changeTab(t.key)} data-testid={`logs-tab-${t.key}`}
            className={`relative px-3 py-2 text-sm transition-colors ${tab === t.key ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"}`}>
            {t.label}
            {tab === t.key && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-white" />}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/[0.06] bg-[#0E0E10] px-3 py-2.5" data-testid="logs-filters">
        <div className="relative w-full sm:w-[220px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search logs..." data-testid="logs-search"
            className="h-9 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-8 text-sm" />
        </div>
        <div className="w-full sm:w-[190px]">
          <DateRangePicker from={range.from} to={range.to} onChange={(r) => { setRange(r); setPage(1); }} placeholder="Date range" testId="logs-date-range" />
        </div>
        {isSuper && (
          <div className="w-full sm:w-[190px]"><SearchSelect value={tenant} onChange={(v) => { setTenant(v); setPage(1); }} options={tenantOpts} testId="logs-filter-tenant" widthClass="w-[240px]" triggerClass="h-9 rounded-lg" /></div>
        )}
        {!isSuper && !isBuyer && tab === "system" && (
          <div className="w-full sm:w-[190px]"><SearchSelect value={uid} onChange={(v) => { setUid(v); setPage(1); }} options={userOpts} testId="logs-filter-user" widthClass="w-[240px]" triggerClass="h-9 rounded-lg" /></div>
        )}
        {!isSuper && !isBuyer && tab === "inbound" && (
          <>
            <div className="w-full sm:w-[190px]"><SearchSelect value={advId} onChange={(v) => { setAdvId(v); setPage(1); }} options={advOpts} testId="logs-filter-advertiser" widthClass="w-[240px]" triggerClass="h-9 rounded-lg" /></div>
            <div className="w-full sm:w-[190px]"><SearchSelect value={buyerId} onChange={(v) => { setBuyerId(v); setPage(1); }} options={buyerOpts} testId="logs-filter-buyer" widthClass="w-[240px]" triggerClass="h-9 rounded-lg" /></div>
          </>
        )}
        {!isSuper && !isBuyer && tab === "outbound" && (
          <div className="w-full sm:w-[190px]"><SearchSelect value={buyerId} onChange={(v) => { setBuyerId(v); setPage(1); }} options={buyerOpts} testId="logs-filter-buyer" widthClass="w-[240px]" triggerClass="h-9 rounded-lg" /></div>
        )}
        {tab === "inbound" && (
          <label className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground" title="Show diagnostic rows (unmatched subids) hidden from the main list">
            <input type="checkbox" checked={showDiag} onChange={(e) => { setShowDiag(e.target.checked); setPage(1); }}
              data-testid="logs-show-diagnostics" className="h-3.5 w-3.5 accent-white" />
            Diagnostics
          </label>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setSort((s) => (s === "desc" ? "asc" : "desc"))} data-testid="logs-sort"
            className="h-9 gap-1.5 rounded-lg px-2.5 text-sm text-muted-foreground hover:text-foreground"><ArrowUpDown className="h-3.5 w-3.5" /> {sort === "desc" ? "Newest" : "Oldest"}</Button>
          <Button size="sm" variant="ghost" onClick={resetFilters} data-testid="logs-reset" className="h-9 rounded-lg px-2.5 text-sm text-muted-foreground hover:text-foreground">Reset</Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-white/[0.06] bg-[#0E0E10]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]" data-testid={`logs-table-${tab}`}>
            <thead className="border-b border-white/[0.06] bg-white/[0.02]">
              <tr>
                <th className={th}>Time</th>
                {tab === "system" && <><th className={th}>Category</th><th className={th}>Action</th><th className={th}>Message</th><th className={th}>Actor</th></>}
                {tab === "inbound" && <><th className={th}>Result</th>{isSuper && <th className={th}>Tenant</th>}<th className={th}>Advertiser</th><th className={th}>Subid / Click</th><th className={th}>Type</th><th className={th}>Payment</th><th className={th}>Conversion</th><th className={th}></th></>}
                {tab === "outbound" && <><th className={th}>Result</th><th className={th}>HTTP</th><th className={th}>Postback</th><th className={th}>Buyer</th><th className={th}>Conv / Order</th><th className={th}></th></>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {isLoading ? (
                <tr><td className="px-3 py-10 text-center text-sm text-muted-foreground" colSpan={9}>Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td className="px-3 py-10 text-center text-sm text-muted-foreground" colSpan={9}>No logs found.</td></tr>
              ) : rows.map((r, i) => {
                const rid = r.id || i;
                const isOpen = expanded === rid;
                return (
                  <React.Fragment key={rid}>
                    <tr className={`hover:bg-white/[0.02] ${tab === "system" ? "cursor-pointer" : ""}`}
                      onClick={tab === "system" ? () => setDetail(r) : undefined}
                      data-testid={tab === "system" ? `log-row-${rid}` : undefined}>
                      <td className={`${td} whitespace-nowrap text-muted-foreground`}>{fmtDateTime(r.created_at)}</td>
                      {tab === "system" && <>
                        <td className={td}><span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs">{r.category}</span></td>
                        <td className={`${td} font-mono text-xs text-muted-foreground`}>{r.action}</td>
                        <td className={td}>{r.message}</td>
                        <td className={`${td} text-muted-foreground`}>{r.actor || "—"}</td>
                      </>}
                      {tab === "inbound" && <>
                        <td className={td}><StatusBadge status={r.matched ? (r.status === "created" ? "approved" : "hold") : "trash"} label={r.status} /></td>
                        {isSuper && <td className={`${td} text-muted-foreground`}>{r.tenant_name || "—"}</td>}
                        <td className={`${td} text-muted-foreground`}>{r.advertiser_name || "—"}</td>
                        <td className={`${td} font-mono text-xs`}>{r.subid || "—"}</td>
                        <td className={td}>{r.conversion_type || "—"}</td>
                        <td className={td}>{r.payment_status || "—"}</td>
                        <td className={`${td} font-mono text-xs`}>{r.order_id || (r.conversion_id ? `${r.conversion_id.slice(0, 8)}…` : "—")}</td>
                        <td className={td}><button onClick={() => setExpanded(isOpen ? null : rid)} data-testid={`logs-expand-${rid}`} className="text-muted-foreground hover:text-foreground"><ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></button></td>
                      </>}
                      {tab === "outbound" && <>
                        <td className={td}><StatusBadge status={r.success ? "approved" : "trash"} label={r.success ? "success" : "error"} /></td>
                        <td className={`${td} font-mono text-xs`}>{r.http_status || 0}{r.replay ? " ⟳" : ""}</td>
                        <td className={td}>{r.postback_name || "—"}</td>
                        <td className={`${td} text-muted-foreground`}>{r.buyer_name || (r.buyer_id ? r.buyer_id.slice(0, 8) : "—")}</td>
                        <td className={`${td} font-mono text-xs`}>{r.order_id || (r.conversion_id ? `${r.conversion_id.slice(0, 8)}…` : "—")}</td>
                        <td className={td}><button onClick={() => setExpanded(isOpen ? null : rid)} data-testid={`logs-expand-${rid}`} className="text-muted-foreground hover:text-foreground"><ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /></button></td>
                      </>}
                    </tr>
                    {isOpen && (tab === "inbound" || tab === "outbound") && (
                      <tr className="bg-[#0A0A0A]"><td colSpan={9} className="px-4 py-3">
                        <div className="space-y-2 text-xs">
                          <div><span className="text-muted-foreground/60">URL: </span><span className="break-all font-mono text-foreground/80">{r.received_url || r.url || "—"}</span></div>
                          {tab === "outbound" && r.final_url && r.final_url !== r.url && <div><span className="text-muted-foreground/60">Final URL: </span><span className="break-all font-mono text-foreground/70">{r.final_url}</span></div>}
                          {tab === "outbound" && <div><span className="text-muted-foreground/60">HTTP status: </span><span className="font-mono text-foreground/70">{r.http_status ?? 0}</span></div>}
                          {tab === "inbound" && r.click_id && <div><span className="text-muted-foreground/60">Click ID: </span><span className="break-all font-mono text-foreground/70">{r.click_id}</span></div>}
                          {tab === "inbound" && r.conversion_id && <div><span className="text-muted-foreground/60">Conversion ID: </span><span className="break-all font-mono text-foreground/70">{r.conversion_id}</span></div>}
                          {tab === "inbound" && r.duplicate_reason && <div><span className="text-muted-foreground/60">Duplicate: </span><span className="break-all font-mono text-[#EAB308]/90">{r.duplicate_reason}</span></div>}
                          {r.response_body && <div><span className="text-muted-foreground/60">Response: </span><span className="break-all font-mono text-foreground/70">{r.response_body}</span></div>}
                          {r.error && <div><span className="text-muted-foreground/60">Error: </span><span className="break-all font-mono text-red-400/80">{r.error}</span></div>}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.06] px-3 py-2.5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="text-xs">Rows per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="logs-page-size" className="h-8 w-[72px] border-white/[0.1] bg-[#0A0A0A] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)} className="text-xs">{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="logs-total">{total === 0 ? "0 records" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="logs-prev" className="h-8 gap-1 px-2 text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="px-1 text-xs">Page {page} / {maxPage}</span>
              <Button size="sm" variant="ghost" disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)} data-testid="logs-next" className="h-8 gap-1 px-2 text-muted-foreground hover:text-foreground"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {canClear && <ClearLogsDialog open={clearOpen} onOpenChange={setClearOpen} isSuper={isSuper} tenantOpts={tenantOpts} activeType={tab === "system" || tab === "inbound" ? tab : "outbound"} />}
      <LogDetailDrawer log={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
